/**
 * Admin membership subscription actions (DNKADM-0219 / DNKAPI-0373).
 *
 * Pause/resume/cancel are bearer-only `/v2/admin/**` writes keyed by contact id.
 * They take no request body and return the subscription's current state. The API is
 * idempotent for same-state pause/resume, so any 200 response is a success. Cancel
 * carries the operator-chosen `?mode=` (DNKADM-0232) — see `MembershipCancelMode`.
 */
import { request } from './client';

const PATH = '/v2/admin/memberships';

type MembershipSubscriptionStatus = 'active' | 'past_due' | 'canceled' | 'paused';

export interface MembershipSubscriptionActionResponse {
  subscriptionId: string | null;
  subscriptionStatus: MembershipSubscriptionStatus | string | null;
  contactId: string | null;
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function asText(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length === 0 ? null : trimmed;
}

function parseSubscriptionActionResponse(body: unknown): MembershipSubscriptionActionResponse {
  const o = isObject(body) ? body : {};
  return {
    subscriptionId: asText(o['subscriptionId']),
    subscriptionStatus: asText(o['subscriptionStatus'])?.toLowerCase() ?? null,
    contactId: asText(o['contactId']),
  };
}

function subscriptionPath(contactId: string): string {
  return `${PATH}/${encodeURIComponent(contactId)}/subscription`;
}

export async function pauseMembershipSubscription(
  contactId: string,
): Promise<MembershipSubscriptionActionResponse> {
  const body = await request<unknown>('POST', `${subscriptionPath(contactId)}/pause`);
  return parseSubscriptionActionResponse(body);
}

export async function resumeMembershipSubscription(
  contactId: string,
): Promise<MembershipSubscriptionActionResponse> {
  const body = await request<unknown>('POST', `${subscriptionPath(contactId)}/resume`);
  return parseSubscriptionActionResponse(body);
}

/**
 * When a cancellation takes effect (DNKADM-0232). `at_period_end` keeps the member's
 * access until the current period closes and is the safe default; `immediate` ends it
 * now. The operator chooses in the cancel dialog — the FE never picks silently.
 *
 * These are the wire tokens sent as `?mode=`; isolated here so a contract rename is a
 * one-line edit.
 */
export type MembershipCancelMode = 'at_period_end' | 'immediate';

/** The safe default offered first in the cancel dialog. */
export const DEFAULT_MEMBERSHIP_CANCEL_MODE: MembershipCancelMode = 'at_period_end';

export async function cancelMembershipSubscription(
  contactId: string,
  mode: MembershipCancelMode,
): Promise<MembershipSubscriptionActionResponse> {
  const body = await request<unknown>('DELETE', subscriptionPath(contactId), {
    query: { mode },
  });
  return parseSubscriptionActionResponse(body);
}
